import {
  IssueManagementMode,
  PanelDisplayRequest,
  I3DCallbacks,
  TcIssueManagement,
} from '@trimble/tc-issue-management-ui';
import {
  on,
  createMemo,
  createResource,
  createSignal,
  createEffect,
  JSX,
  onCleanup,
  Show,
} from 'solid-js';
import { ErrorState } from '../components/LoadingState';
import { useAuth } from '@trimble/trimble-id-solidjs';
import {
  collectVisibleModels,
  getVisiblePointCloudIds,
  useApiServices,
  useCapabilityRegistryContext,
  useDataContext,
  useFeatureExtractionContext,
  usePaneContext,
} from '@mobile-mapping/providers';
import { useTranslations } from '@trimble/solidjs-utils';
import {
  IssueMarkerIconCoordinator,
  IssueMarkerPickCoordinator,
} from '@trimble/tc-issue-management-web3d-utilities';
import { t } from '@trimble/i18n';
import { Spinner } from '@tcweb/connect-ui';
import { ILogger } from '@trimble/logging';
import { ViewsCapabilityProvider } from '@trimble/web3d-providers';
import { useLogger } from '@trimble/solidjs-utils';
import { IssueManagementCbStore } from './IssueManagementCbStore';
import {
  type TcRegion,
  useConnectProject,
} from '@trimble/connect-project-providers';
import { createNumarkIssueReservedViewDataConfig } from './create-numark-issue-reserved-view-data-config';

function logger(): ILogger {
  return useLogger().getLogger('IssueManagementTab');
}

function hostInstanceKey(
  projectId: string,
  region: TcRegion,
  instanceId: number
): string {
  return `${projectId}:${region.location}:${String(instanceId)}`;
}

type CreateTopicRequest = {
  readonly type: 'create-topic';
  readonly id: number;
};

export const IssueManagementTab = (props: {
  viewsProvider: ViewsCapabilityProvider;
  createTopicRequest?: CreateTopicRequest;
  onCreateTopicRequestConsumed?(): void;
}): JSX.Element => {
  const { isProductionEnvironment, tcTcpsApi, scanDataApi } = useApiServices();
  const auth = useAuth();
  const { selectedProject, selectedRegion } = useConnectProject();
  const { capabilityRegistry } = useCapabilityRegistryContext();
  const { dataState, dataLoadingService, activeItems } = useDataContext();
  const { layouts, setLayouts } = usePaneContext();
  const featureExtractionContext = useFeatureExtractionContext();
  const { translations, translationsReady, refetchTranslations } =
    useTranslations();

  const [showAllMarker, setShowAllMarker] = createSignal(true);
  const [displayRequest, setDisplayRequest] = createSignal<
    PanelDisplayRequest | undefined
  >(undefined);
  const [issueManagementInstanceId, setIssueManagementInstanceId] =
    createSignal(0);

  const numarkReservedViewData = createNumarkIssueReservedViewDataConfig({
    tcTcpsApi,
    scanDataApi,
    getRegion: () => selectedRegion(),
    getProjectId: () => selectedProject()?.id,
    capabilityRegistry,
    layouts,
    activeItems,
    dataState,
    featureExtractionContext,
    logger: logger(),
  });

  const savePointCloudsForCreatedView = (viewId: string): void => {
    const region = selectedRegion();
    const project = selectedProject();
    if (!region || !project) return;

    const visiblePointCloudIds = getVisiblePointCloudIds(dataState) ?? [];
    scanDataApi
      .savePointCloudsForView(region, project.id, viewId, visiblePointCloudIds)
      .then(() => {
        logger().debug('Point clouds saved for view', {
          viewId,
          visiblePointCloudIds,
        });
      })
      .catch((error: unknown) => {
        logger().error('Failed to save point clouds for view', {
          viewId,
          error,
        });
      });
  };

  createEffect(
    on(
      () => props.createTopicRequest,
      (request) => {
        if (!request) return;
        setIssueManagementInstanceId(request.id);
        setDisplayRequest({ mode: IssueManagementMode.Create });
        props.onCreateTopicRequestConsumed?.();
      }
    )
  );

  const [accessToken, { refetch: refetchAccessToken }] = createResource(
    async () => {
      return await auth().getAccessTokenSilently();
    }
  );

  // Compare by ids, not object identity. `Show keyed` remounts on a new `when`
  // value, and remount runs onCleanup (cancelPick + setMarkers([])), which
  // drops an in-flight marker pick. Project/region query updates often
  // replace the object while the id stays the same.
  // Read `.error` / `.latest` instead of `accessToken()`: a failed resource
  // throws from the accessor, which keeps this memo (and the outer error
  // `Show`) from switching off the spinner.
  const hostInstance = createMemo(
    () => {
      if (accessToken.error || accessToken.state === 'errored') {
        return undefined;
      }
      const token = accessToken.latest;
      const project = selectedProject();
      const region = selectedRegion();
      if (!token || !project || !translationsReady() || !region) {
        return undefined;
      }
      return {
        key: hostInstanceKey(project.id, region, issueManagementInstanceId()),
        project,
        region,
        accessToken: token,
      };
    },
    undefined,
    { equals: (previous, next) => previous?.key === next?.key }
  );

  const resourceError = (): Error | string | undefined => {
    if (accessToken.error) return accessToken.error as Error;
    if (translations.error) return translations.error as Error;
    return undefined;
  };

  const retry = (): void => {
    if (accessToken.error) void refetchAccessToken();
    if (translations.error) refetchTranslations();
  };

  return (
    <Show
      when={!resourceError()}
      fallback={
        <div
          class="flex flex-col items-center justify-center gap-3 w-full h-full p-4 bg-white"
          data-testid="issue-management-error"
        >
          <ErrorState
            error={resourceError()}
            prefix={t('mobileMapping.issueManagement.loadError')}
          />
          <button
            class="text-sm text-blue-600 underline cursor-pointer"
            onClick={retry}
            data-testid="issue-management-retry"
          >
            {t('mobileMapping.issueManagement.retry')}
          </button>
        </div>
      }
    >
      <Show
        when={hostInstance()}
        fallback={
          <div class="w-full h-[300px]">
            <Spinner show={true} />
          </div>
        }
        keyed
      >
        {(host) => {
          const iconCoordinator = capabilityRegistry.get(
            IssueMarkerIconCoordinator
          )();
          const pickCoordinator = capabilityRegistry.get(
            IssueMarkerPickCoordinator
          )();

          onCleanup(() => {
            pickCoordinator?.cancelPick();
            iconCoordinator?.removeMarker();
            iconCoordinator?.setMarkers([]);
            iconCoordinator?.setSelectedMarkerId(null);
          });

          const callbacks = createMemo<I3DCallbacks>(() => ({
            ...IssueManagementCbStore({
              projectId: host.project.id,
              capabilityRegistry,
              dataState,
              region: host.region,
              tcTcpsApi,
              dataLoadingService,
              layouts,
              setLayouts,
              pushSnapshot: numarkReservedViewData.pushSnapshot,
              beginCreateCapture: numarkReservedViewData.beginCreateCapture,
              pushCreateSnapshot: numarkReservedViewData.pushCreateSnapshot,
              featureExtractionContext,
            }),
            onShowAllMarkerChange: (showAll) => setShowAllMarker(showAll),
            onIssueViewCreatedSuccess: (viewId) => {
              savePointCloudsForCreatedView(viewId);
            },
            onCommentViewCreatedSuccess: (viewId) => {
              savePointCloudsForCreatedView(viewId);
            },
            onCommentViewCaptureCancelled: (issueId) => {
              numarkReservedViewData.popLastSnapshot(issueId);
            },
            onIssueViewCreateError: (issueId) => {
              numarkReservedViewData.popLastSnapshot(issueId);
            },
          }));
          return (
            <TcIssueManagement
              projectId={host.project.id}
              accessToken={host.accessToken}
              environment={isProductionEnvironment ? 'prod' : 'stage'}
              threeDCallbacks={callbacks()}
              reservedViewDataConfig={
                numarkReservedViewData.reservedViewDataConfig
              }
              featureConfig={{ issueViewPreview: { enabled: true } }}
              modelIds={collectVisibleModels(dataState)}
              showAllMarker={showAllMarker()}
              displayRequest={displayRequest()}
            />
          );
        }}
      </Show>
    </Show>
  );
};
